/**
 * Triple-store persistence for the long-lived `true_device_id`.
 *
 * SEON-parity: a single device should keep a stable identifier even when one
 * storage layer is cleared. We mirror the id across three independent layers
 * and reconcile on read:
 *   1. localStorage      (fast, survives reloads)
 *   2. IndexedDB         (survives some "clear cookies" flows)
 *   3. first-party cookie (survives localStorage clears; long max-age)
 *
 * On read we take the first id we find, then re-seed every empty layer so the
 * id "heals" across partial clears. If nothing exists, we mint a new one.
 *
 * Privacy: callers pass `persistence:false` (GDPR mode) to skip all of this and
 * fall back to an ephemeral, per-session id.
 */

import { uuid } from "../util/sha256.ts";

const KEY = "gs_tdid";
const COOKIE_MAX_AGE = 60 * 60 * 24 * 400; // ~400d, browser cap
const DB_NAME = "gs_web_sdk";
const STORE_NAME = "kv";

function readLocalStorage(): string | null {
  try {
    return window.localStorage.getItem(KEY);
  } catch {
    return null;
  }
}

function writeLocalStorage(id: string): void {
  try {
    window.localStorage.setItem(KEY, id);
  } catch {
    /* private mode / disabled */
  }
}

function readCookie(): string | null {
  try {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + KEY + "=([^;]*)"),
    );
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

function writeCookie(id: string): void {
  try {
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie =
      `${KEY}=${encodeURIComponent(id)}; Max-Age=${COOKIE_MAX_AGE}; Path=/; SameSite=Lax${secure}`;
  } catch {
    /* cookies disabled */
  }
}

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    try {
      if (typeof indexedDB === "undefined") return resolve(null);
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        try {
          req.result.createObjectStore(STORE_NAME);
        } catch {
          /* already exists */
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
      // Safari can hang on private mode — guard with a timeout.
      setTimeout(() => resolve(null), 600);
    } catch {
      resolve(null);
    }
  });
}

async function readIndexedDb(): Promise<string | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readonly");
      const req = tx.objectStore(STORE_NAME).get(KEY);
      req.onsuccess = () => resolve(typeof req.result === "string" ? req.result : null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function writeIndexedDb(id: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  await new Promise<void>((resolve) => {
    try {
      const tx = db.transaction(STORE_NAME, "readwrite");
      tx.objectStore(STORE_NAME).put(id, KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => resolve();
    } catch {
      resolve();
    }
  });
}

export interface PersistentIdResult {
  id: string;
  present_before: boolean;
}

/**
 * Resolve the stable `true_device_id`, healing any empty storage layer.
 * When `persist` is false, returns a fresh ephemeral id without writing.
 */
export async function resolveTrueDeviceId(persist: boolean): Promise<PersistentIdResult> {
  if (!persist) {
    return { id: uuid(), present_before: false };
  }

  const ls = readLocalStorage();
  const ck = readCookie();
  const idb = await readIndexedDb();

  const existing = ls || ck || idb || null;
  const id = existing ?? uuid();

  // Re-seed every layer that is missing or stale.
  if (ls !== id) writeLocalStorage(id);
  if (ck !== id) writeCookie(id);
  if (idb !== id) await writeIndexedDb(id);

  return { id, present_before: existing !== null };
}
